import { Dispatch, useEffect, useState } from 'react';
import styled from 'styled-components';
import { getPreviousQuestionsValue } from 'src/common/utils/functions';
import { DEFAULT_ERROR_STATE } from 'src/containers/client-assessment/types';
import Card from './Card';
import { AllocatedFunds, HandleChangeParams, ItemsWrapperProps } from './types';
import { mobileBreakpoint } from 'src/containers/client-assessment/mobileBreakpoint';

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  justify-content: center;
`;

const Remaining = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  p {
    color: #525863;
    font-weight: 500 0.875rem Inter, sans-serif;
  }
  .main__box {
    margin: 0 0.5em;
    padding: 0.75em;
    border-radius: 3px;
    display: flex;
    align-items: center;
    span {
      color: #033e3e;
      font-size: 16px;
      font: 500 1.0625rem/1.09563125rem Inter, sans-serif;
    }
    .remaining__funds {
      font: 500 1.0625rem/1.09563125rem Inter, sans-serif;
      margin: 0;
      color: ${props => props.theme.green.regular};
    }
  }
  @media ${mobileBreakpoint.mobile} {
    font-size: 14px;
    .main__box {
      font-size: 18px;
    }
  }
`;

const ItemsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  justify-content: center;
  align-items: center;
  overflow-y: scroll;
  @media ${mobileBreakpoint.mobile} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    height: 40vh !important;
    box-shadow: 0 5px 4px -4px #0000002b;
  }
  @media screen and (min-width: 420px) {
    max-height: 56vh;
  }
  @media screen and (max-height: 480px) {
    height: 30vh !important;
  }
`;

type Props = ItemsWrapperProps;

function CausesInput({
  label,
  selectedQuestionId,
  questionnaireValue,
  setQuestionnaireValue,
  options,
  questions,
  attributeKey,
  error,
  setError,
}: Props) {
  const [causesValue, setCausesValue] = useState(1000);
  const [allocatedFunds, setAllocatedFunds] = useState<AllocatedFunds>({});
  const [newOptions, setNewOptions] = useState<any[]>([]);

  useEffect(() => {
    const currentQuestionValue = questionnaireValue[selectedQuestionId];
    if (currentQuestionValue) {
      const uptilNowAllocatedFunds = Object.values(currentQuestionValue).reduce(
        (total: number, item: any) => (total += item),
        0,
      );
      setCausesValue(1000 - uptilNowAllocatedFunds);
      setAllocatedFunds(currentQuestionValue);
    } else {
      setCausesValue(1000);
      setAllocatedFunds({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label]);

  useEffect(() => {
    if (attributeKey === 'causes_resonates_with_you') {
      const value = getPreviousQuestionsValue(
        'tap_these_causes',
        questions,
        questionnaireValue,
      );
      setNewOptions(
        options.filter(option => {
          if (option?.parentCategory && value) {
            const parent = option?.parentCategory
              .toLowerCase()
              .split(' ')
              .join('_');
            const oldValues = Object.keys(value).filter(key => value[key] > 0);
            return oldValues.includes(parent);
          }
          return false;
        }),
      );
    } else {
      setNewOptions(options);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributeKey]);

  useEffect(() => {
    const isTotalEqualToThousand =
      Object.values(allocatedFunds).reduce(
        (total, item) => (total += item),
        0,
      ) > 99;
    if (isTotalEqualToThousand) {
      setQuestionnaireValue({
        ...questionnaireValue,
        [selectedQuestionId]: allocatedFunds,
      });
      setError(DEFAULT_ERROR_STATE);
    } else {
      setQuestionnaireValue({
        ...questionnaireValue,
        [selectedQuestionId]: {},
      });
      setError({ message: '', valid: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocatedFunds]);

  const handleActionClick = ({ type, property }: HandleChangeParams) => {
    const multipleValue = 100;
    const objectValueForOldProperty = allocatedFunds[property];
    if (type === 'INC') {
      if (causesValue > 0) {
        setCausesValue(causesValue - multipleValue);
        setAllocatedFunds({
          ...allocatedFunds,
          [property]: (objectValueForOldProperty ?? 0) + multipleValue,
        });
      }
    } else {
      if (objectValueForOldProperty) {
        if (causesValue < 1000) {
          setCausesValue(causesValue + multipleValue);
          setAllocatedFunds({
            ...allocatedFunds,
            [property]: (objectValueForOldProperty ?? 0) - multipleValue,
          });
        }
      }
    }
  };
  return (
    <Wrapper>
      <Remaining>
        <p>You have</p>
        <div className="main__box">
          <h5 className="remaining__funds">{`$${causesValue} `}</h5>
          <span>{` / $1000`}</span>
        </div>
        <p>To Allocate!</p>
      </Remaining>
      <ItemsWrapper>
        {newOptions.map((item, index) => (
          <Card
            key={index.toString()}
            item={{
              label: item.label,
              value: item.value,
              image: '',
              imageTitle: item.imageTitle,
            }}
            allocatedFunds={allocatedFunds}
            remainingFunds={causesValue}
            handleActionClick={handleActionClick}
          />
        ))}
      </ItemsWrapper>
    </Wrapper>
  );
}

export default CausesInput;
